import React from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Alert,
  Chip,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Skeleton,
  Stack,
  IconButton,
  Tooltip,
  Container,
  TextField,
  InputAdornment,
  Button,
  Fade,
  Collapse,
  alpha,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Refresh as RefreshIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Phone as PhoneIcon,
  Payment as PaymentIcon,
  FilterList as FilterListIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Cancel as CancelIcon,
  AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material';
import {
  useGetDepositsQuery,
  useLazyGetDepositsQuery,
} from '../../store/api/dashboardApi';
import { formatCurrency } from '../../utils/formatters';

const Deposits = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // Search and filter state
  const [searchInput, setSearchInput] = React.useState('');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchMode, setSearchMode] = React.useState(false);
  const [paymentSourceFilter, setPaymentSourceFilter] = React.useState('');
  const [showFilters, setShowFilters] = React.useState(false);

  // Get filters from Redux (including date range)
  const dashboardFilters = useSelector((state) => state.dashboard.filters);

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: isMobile ? 10 : 25,
  });

  // Main query – skipped during search mode, uses dashboard filters including date range
  const {
    data: depositsData,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useGetDepositsQuery(dashboardFilters, {
    skip: searchMode,
  });

  // Lazy search query
  const [
    searchDeposits,
    { data: searchResults, isLoading: isSearching, error: searchError },
  ] = useLazyGetDepositsQuery();

  React.useEffect(() => {
    setPaginationModel((prev) => ({
      ...prev,
      pageSize: isMobile ? 10 : 25,
    }));
  }, [isMobile]);

  // Handle search input change with debounce
  const searchTimeoutRef = React.useRef();

  const performSearch = (query, paymentSource) => {
    const searchParams = {};

    if (query) searchParams.search = query;
    if (paymentSource) searchParams.provider = paymentSource;

    setSearchQuery(query);
    setSearchMode(true);
    searchDeposits(searchParams);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setPaymentSourceFilter('');
    setSearchMode(false);
    setShowFilters(false);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
  };

  const handleSearchInputChange = (event) => {
    const value = event.target.value;
    setSearchInput(value);

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(() => {
      const trimmedQuery = value.trim();
      if (trimmedQuery) {
        performSearch(trimmedQuery, paymentSourceFilter);
      } else {
        clearSearch();
      }
    }, 500);
  };

  const handlePaymentSourceChange = (event) => {
    const value = event.target.value;
    setPaymentSourceFilter(value);

    if (searchInput.trim() || value) {
      performSearch(searchInput.trim(), value);
    }
  };

  // Display helpers
  const displayData = searchMode
    ? searchResults || { summary: { recordsReturned: 0, totalAmount: 0 }, deposits: [] }
    : depositsData;

  const summary = displayData?.summary || {};
  const deposits = displayData?.deposits || [];

  const rows = React.useMemo(
    () =>
      deposits.map((deposit, index) => ({
        id: deposit.deposit_id || `deposit-${index}`,
        ...deposit,
        transactionDate: deposit.transaction_date
          ? new Date(deposit.transaction_date)
          : null,
        createdAt: deposit.created_at ? new Date(deposit.created_at) : null,
        // Ensure status is uppercase for consistent display
        status: (deposit.status || 'UNKNOWN').toUpperCase(),
      })),
    [deposits]
  );

  // Status helper functions
  const getStatusColor = (status) => {
    const statusUpper = (status || '').toUpperCase();
    switch (statusUpper) {
      case 'COMPLETED':
      case 'SUCCESS':
        return 'success';
      case 'PENDING':
      case 'PROCESSING':
        return 'warning';
      case 'FAILED':
      case 'ERROR':
      case 'REJECTED':
        return 'error';
      default:
        return 'info';
    }
  };

  const getStatusIcon = (status) => {
    const statusUpper = (status || '').toUpperCase();
    switch (statusUpper) {
      case 'COMPLETED':
      case 'SUCCESS':
        return <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />;
      case 'PENDING':
      case 'PROCESSING':
        return <HourglassEmptyIcon sx={{ fontSize: 16, color: 'warning.main' }} />;
      case 'FAILED':
      case 'ERROR':
      case 'REJECTED':
        return <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />;
      default:
        return <HourglassEmptyIcon sx={{ fontSize: 16, color: 'info.main' }} />;
    }
  };

  const getColumns = React.useCallback(() => {
    const baseColumns = [
      {
        field: 'phone',
        headerName: 'Phone',
        width: 140,
        minWidth: 120,
        flex: 1,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                fontWeight: searchMode ? 'bold' : 'normal',
                color: searchMode ? 'primary.main' : 'inherit',
              }}
            >
              {params.value}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'amount',
        headerName: 'Amount',
        width: isMobile ? 100 : 120,
        type: 'number',
        minWidth: 90,
        valueFormatter: (value) => formatCurrency(value ?? 0),
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
            <Typography
              variant="body2"
              color="success.main"
              sx={{ fontWeight: 'medium', fontSize: { xs: '0.75rem', md: '0.875rem' } }}
            >
              {formatCurrency(params.value ?? 0)}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: isMobile ? 100 : 120,
        minWidth: 90,
        renderCell: (params) => (
          <Chip
            label={params.value || 'UNKNOWN'}
            color={getStatusColor(params.value)}
            size="small"
            variant="outlined"
            icon={getStatusIcon(params.value)}
            sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
          />
        ),
      },
      {
        field: 'provider',
        headerName: 'Provider',
        width: isMobile ? 100 : 140,
        minWidth: 90,
        renderCell: (params) => (
          <Chip
            label={params.value || 'N/A'}
            color="primary"
            size="small"
            variant="outlined"
            sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
          />
        ),
      },
    ];

    if (!isMobile) {
      baseColumns.push(
        {
          field: 'deposit_id',
          headerName: 'Deposit ID',
          width: 160,
          minWidth: 100,
          renderCell: (params) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReceiptIcon sx={{ fontSize: 16, color: 'info.main' }} />
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: { xs: '0.7rem', md: '0.8rem' },
                  color: 'info.main',
                }}
              >
                {params.value ? `${params.value.substring(0, 12)}...` : 'N/A'}
              </Typography>
            </Box>
          ),
        },
        {
          field: 'transactionDate',
          headerName: 'Date',
          width: 120,
          type: 'dateTime',
          valueFormatter: (value) => (value ? new Date(value).toLocaleDateString() : 'N/A'),
        }
      );
    }

    if (!isTablet) {
      baseColumns.push(
        {
          field: 'deposit_type',
          headerName: 'Type',
          width: 100,
          renderCell: (params) => (
            <Chip
              label={params.value || 'N/A'}
              color="secondary"
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
          ),
        },
        {
          field: 'currency',
          headerName: 'Currency',
          width: 80,
          renderCell: (params) => (
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {params.value || 'ZMW'}
            </Typography>
          ),
        },
        {
          field: 'operator',
          headerName: 'Operator',
          width: 100,
          renderCell: (params) => (
            <Typography variant="body2" color="text.secondary">
              {params.value || '-'}
            </Typography>
          ),
        }
      );
    }

    return baseColumns;
  }, [isMobile, isTablet, searchMode]);

  // Unique payment sources
  const paymentSources = React.useMemo(() => {
    const sources = new Set();
    deposits.forEach((d) => {
      if (d.provider) sources.add(d.provider);
    });
    return Array.from(sources).sort();
  }, [deposits]);

  // Calculate status counts (prefer API values, fallback to local calculation)
  const statusCounts = React.useMemo(() => {
    // Use API-provided breakdown if available
    if (summary.statusBreakdown && Object.keys(summary.statusBreakdown).length > 0) {
      // Merge similar statuses (handle both upper and lowercase)
      const breakdown = summary.statusBreakdown;
      return {
        COMPLETED: (breakdown.COMPLETED || 0) + (breakdown.completed || 0) + 
                   (breakdown.SUCCESS || 0) + (breakdown.success || 0) + 
                   (summary.completedCount || 0),
        PENDING: (breakdown.PENDING || 0) + (breakdown.pending || 0) + 
                 (breakdown.PROCESSING || 0) + (breakdown.processing || 0) + 
                 (summary.pendingCount || 0),
        FAILED: (breakdown.FAILED || 0) + (breakdown.failed || 0) + 
                (breakdown.ERROR || 0) + (breakdown.error || 0) + 
                (summary.failedCount || 0),
      };
    }
    // Fallback to local calculation
    const counts = { COMPLETED: 0, PENDING: 0, FAILED: 0 };
    deposits.forEach(d => {
      const s = (d.status || 'UNKNOWN').toUpperCase();
      if (s === 'COMPLETED' || s === 'SUCCESS') counts.COMPLETED++;
      else if (s === 'PENDING' || s === 'PROCESSING') counts.PENDING++;
      else if (s === 'FAILED' || s === 'ERROR' || s === 'REJECTED') counts.FAILED++;
    });
    return counts;
  }, [deposits, summary]);

  const summaryCards = [
    {
      title: 'Total Deposits',
      value: summary.recordsReturned?.toLocaleString() ?? '0',
      color: 'primary.main',
      icon: <ReceiptIcon />,
      bgColor: theme.palette.primary.light,
    },
    {
      title: 'Total Amount',
      value: formatCurrency(summary.totalAmount ?? 0),
      color: 'success.main',
      icon: <AccountBalanceIcon />,
      bgColor: theme.palette.success.light,
    },
    {
      title: 'Success Amount',
      value: formatCurrency(summary.completedAmount ?? 0),
      subtitle: `${summary.successRate ?? 0}% success rate`,
      color: 'info.main',
      icon: <CheckCircleIcon />,
      bgColor: theme.palette.info.light,
    },
    {
      title: 'Unique Depositors',
      value: (summary.uniqueUsers || new Set(deposits.map((d) => d.phone)).size).toLocaleString() ?? '0',
      subtitle: `Avg: ${formatCurrency(summary.averageDeposit ?? 0)}`,
      color: 'warning.main',
      icon: <PeopleIcon />,
      bgColor: theme.palette.warning.light,
    },
  ];

  const LoadingSkeleton = () => (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[1, 2, 3, 4].map((index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Skeleton variant="text" width="60%" height={32} />
                <Skeleton variant="text" width="40%" />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Paper sx={{ height: 400 }}>
        <Skeleton variant="rectangular" height="100%" />
      </Paper>
    </Box>
  );

  if (isLoading && !searchMode) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ py: 2 }}>
          <Skeleton variant="text" width="200px" height={40} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="300px" height={24} sx={{ mb: 3 }} />
          <LoadingSkeleton />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 2 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
            <Typography
              variant={isMobile ? 'h5' : 'h4'}
              component="h1"
              sx={{ fontWeight: 'bold', mb: 1 }}
            >
              Deposits{' '}
              {searchMode && <Chip label="Search Results" color="primary" size="small" sx={{ ml: 1 }} />}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {searchMode
                ? `Search results for: ${searchQuery || 'filtered data'}`
                : 'All-time deposit transactions and payment analysis'}
            </Typography>
          </Box>

          <Tooltip title="Refresh data">
            <IconButton
              onClick={() => refetch()}
              disabled={isFetching || isSearching}
              color="primary"
              sx={{
                bgcolor: 'primary.light',
                '&:hover': { bgcolor: 'primary.main', color: 'white' },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Summary */}
        <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 3 }}>
          {summaryCards.map((card, index) => (
            <Grid item xs={6} sm={6} md={3} key={index}>
              <Card
                sx={{
                  height: '100%',
                  background: `linear-gradient(135deg, ${alpha(card.bgColor, 0.2)} 0%, ${alpha(card.bgColor, 0.1)} 100%)`,
                  border: `1px solid ${alpha(card.bgColor, 0.4)}`,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[8] },
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Box sx={{ color: card.color, display: 'flex', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
                      {card.icon}
                    </Box>
                    <Typography
                      component="div"
                      sx={{
                        color: card.color,
                        fontWeight: 'bold',
                        fontSize: { xs: '1rem', md: '1.25rem' },
                      }}
                    >
                      {card.value}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                  >
                    {card.title}
                  </Typography>
                  {card.subtitle && (
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' }, display: 'block', mt: 0.5 }}
                    >
                      {card.subtitle}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Status Breakdown Cards */}
        {(statusCounts.COMPLETED > 0 || statusCounts.PENDING > 0 || statusCounts.FAILED > 0) && (
          <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 3 }}>
            <Grid item xs={4} sm={4} md={2}>
              <Card sx={{ 
                height: '100%',
                border: `1px solid ${theme.palette.success.main}`,
                borderRadius: 2
              }}>
                <CardContent sx={{ p: 2, textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                    <CheckCircleIcon sx={{ color: 'success.main' }} />
                  </Box>
                  <Typography variant="h6" color="success.main">
                    {statusCounts.COMPLETED}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    COMPLETED
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={4} sm={4} md={2}>
              <Card sx={{ 
                height: '100%',
                border: `1px solid ${theme.palette.warning.main}`,
                borderRadius: 2
              }}>
                <CardContent sx={{ p: 2, textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                    <HourglassEmptyIcon sx={{ color: 'warning.main' }} />
                  </Box>
                  <Typography variant="h6" color="warning.main">
                    {statusCounts.PENDING}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    PENDING
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={4} sm={4} md={2}>
              <Card sx={{ 
                height: '100%',
                border: `1px solid ${theme.palette.error.main}`,
                borderRadius: 2
              }}>
                <CardContent sx={{ p: 2, textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                    <ErrorIcon sx={{ color: 'error.main' }} />
                  </Box>
                  <Typography variant="h6" color="error.main">
                    {statusCounts.FAILED}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    FAILED
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Search & Filters */}
        <Card
          sx={{
            mb: 3,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.background.default,
              0.6
            )} 0%, ${alpha(theme.palette.primary.light, 0.15)} 100%)`,
            border: '1px solid',
            borderColor: 'divider',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': { transform: 'translateY(-1px)', boxShadow: theme.shadows[4] },
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
              {/* Search */}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                <TextField
                  fullWidth
                  placeholder="Search by phone number or deposit ID..."
                  value={searchInput}
                  onChange={handleSearchInputChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment:
                      searchInput && (
                        <InputAdornment position="end">
                          <IconButton onClick={clearSearch} size="small">
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: theme.palette.background.paper } }}
                />

                <Button
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{ minWidth: 'fit-content' }}
                >
                  Filters
                </Button>

                {isSearching && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 'fit-content' }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                      Searching...
                    </Typography>
                  </Box>
                )}
              </Stack>

              {/* Filters */}
              <Collapse in={showFilters}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ pt: 2 }}>
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Provider</InputLabel>
                    <Select
                      value={paymentSourceFilter}
                      label="Provider"
                      onChange={handlePaymentSourceChange}
                    >
                      <MenuItem value="">All Sources</MenuItem>
                      {paymentSources.map((src) => (
                        <MenuItem key={src} value={src}>
                          {src}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Collapse>
            </Stack>
          </CardContent>
        </Card>

        {/* Search Alerts */}
        {searchMode && deposits.length === 0 && (
          <Fade in>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">No deposits found for the current search criteria.</Typography>
              <Button onClick={clearSearch} size="small" sx={{ mt: 1 }}>
                View All Deposits
              </Button>
            </Alert>
          </Fade>
        )}

        {searchMode && deposits.length > 0 && (
          <Fade in>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body2">✅ Found {deposits.length} deposit{deposits.length !== 1 ? 's' : ''}</Typography>
              <Button onClick={clearSearch} size="small" sx={{ mt: 1 }}>
                View All Deposits
              </Button>
            </Alert>
          </Fade>
        )}

        {/* Error */}
        {(error || searchError) && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Error loading deposit data: {error?.message || searchError?.message || 'Unknown error'}
          </Alert>
        )}

        {/* DataGrid */}
        <Paper sx={{ height: { xs: 500, md: 600 }, width: '100%', overflow: 'hidden' }}>
          <DataGrid
            rows={rows}
            columns={getColumns()}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={isMobile ? [10, 25] : [25, 50, 100]}
            rowCount={rows.length}
            loading={isLoading || isFetching || isSearching}
            disableRowSelectionOnClick
            sx={{
              '& .MuiDataGrid-root': { border: 'none' },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid',
                borderBottomColor: 'divider',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: alpha(theme.palette.primary.light, 0.1),
                borderBottom: '2px solid',
                borderBottomColor: 'divider',
              },
              '& .MuiDataGrid-row:hover': { backgroundColor: 'action.hover' },
              '& .MuiDataGrid-row:nth-of-type(even)': {
                backgroundColor: alpha(theme.palette.action.hover, 0.5),
              },
              ...(isMobile && {
                '& .MuiDataGrid-columnHeader': { fontSize: '0.75rem', fontWeight: 'bold' },
                '& .MuiDataGrid-cell': { fontSize: '0.75rem', py: 0.5 },
              }),
            }}
            getRowId={(row) => row.id}
            density={isMobile ? 'compact' : 'standard'}
          />
        </Paper>

        {/* Footer */}
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {searchMode && <Chip label="Filtered" size="small" sx={{ mr: 1 }} />}
            Total Amount: {formatCurrency(summary.totalAmount ?? 0)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Showing {rows.length} deposit{rows.length !== 1 ? 's' : ''}{' '}
            {searchMode ? ' (Search Results)' : ' (All-Time Data)'}
          </Typography>
        </Box>

        {/* No deposits */}
        {!searchMode && !isLoading && deposits.length === 0 && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            No deposit data available. This could be due to no transactions or database connectivity issues.
          </Alert>
        )}
      </Box>
    </Container>
  );
};

export default Deposits;



// // // src/pages/Deposits/Deposits.jsx
// import React from 'react';
// import { useSelector } from 'react-redux';
// import { 
//   Box, 
//   Typography, 
//   Paper, 
//   Grid, 
//   Card, 
//   CardContent,
//   Alert,
//   Chip,
//   CircularProgress,
//   useTheme,
//   useMediaQuery,
//   Skeleton,
//   Stack,
//   IconButton,
//   Tooltip,
//   Container,
//   TextField,
//   InputAdornment,
//   Button,
//   Fade,
//   Collapse,
//   alpha,
//   MenuItem,
//   FormControl,
//   InputLabel,
//   Select
// } from '@mui/material';
// import { DataGrid } from '@mui/x-data-grid';
// import { 
//   Refresh as RefreshIcon,
//   AccountBalance as AccountBalanceIcon,
//   Receipt as ReceiptIcon,
//   People as PeopleIcon,
//   TrendingUp as TrendingUpIcon,
//   Search as SearchIcon,
//   Clear as ClearIcon,
//   Phone as PhoneIcon,
//   Payment as PaymentIcon,
//   FilterList as FilterListIcon
// } from '@mui/icons-material';
// import { selectCurrentFiltersAsApiParams } from '../../store/slices/dashboardSlice';
// import { useGetDepositsQuery, useLazyGetDepositsQuery } from '../../store/api/dashboardApi';

// const Deposits = () => {
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('md'));
//   const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
//   // Search and filter state
//   const [searchInput, setSearchInput] = React.useState('');
//   const [searchQuery, setSearchQuery] = React.useState('');
//   const [searchMode, setSearchMode] = React.useState(false);
//   const [paymentSourceFilter, setPaymentSourceFilter] = React.useState('');
//   const [showFilters, setShowFilters] = React.useState(false);
  
//   const allTimeFilters = {};
  
//   const [paginationModel, setPaginationModel] = React.useState({
//     page: 0,
//     pageSize: isMobile ? 10 : 25,
//   });

//   // Main query - skipped during search mode
//   const { 
//     data: depositsData, 
//     isLoading, 
//     error,
//     refetch,
//     isFetching 
//   } = useGetDepositsQuery(allTimeFilters, {
//     skip: searchMode,
//   });

//   // Lazy search query
//   const [
//     searchDeposits, 
//     { 
//       data: searchResults, 
//       isLoading: isSearching, 
//       error: searchError 
//     }
//   ] = useLazyGetDepositsQuery();

//   React.useEffect(() => {
//     setPaginationModel(prev => ({
//       ...prev,
//       pageSize: isMobile ? 10 : 25
//     }));
//   }, [isMobile]);

//   // Handle search input change with debounce
//   const searchTimeoutRef = React.useRef();

//   const handleSearchInputChange = (event) => {
//     const value = event.target.value;
//     setSearchInput(value);
    
//     if (searchTimeoutRef.current) {
//       clearTimeout(searchTimeoutRef.current);
//     }
    
//     searchTimeoutRef.current = setTimeout(() => {
//       const trimmedQuery = value.trim();
//       if (trimmedQuery) {
//         performSearch(trimmedQuery, paymentSourceFilter);
//       } else {
//         clearSearch();
//       }
//     }, 500); // 500ms debounce
//   };

//   const handlePaymentSourceChange = (event) => {
//     const value = event.target.value;
//     setPaymentSourceFilter(value);
    
//     if (searchInput.trim() || value) {
//       performSearch(searchInput.trim(), value);
//     }
//   };

//   const performSearch = (query, paymentSource) => {
//     const searchParams = {};
    
//     if (query) {
//       searchParams.search = query;
//     }
    
//     if (paymentSource) {
//       searchParams.paymentSource = paymentSource;
//     }
    
//     setSearchQuery(query);
//     setSearchMode(true);
//     searchDeposits(searchParams);
//   };

//   const clearSearch = () => {
//     setSearchInput('');
//     setSearchQuery('');
//     setPaymentSourceFilter('');
//     setSearchMode(false);
//     setShowFilters(false);
//     if (searchTimeoutRef.current) {
//       clearTimeout(searchTimeoutRef.current);
//     }
//   };

//   // Get display data based on search mode
//   const getDisplayData = () => {
//     if (searchMode) {
//       return searchResults || {
//         summary: { recordsReturned: 0, totalAmount: 0 },
//         deposits: []
//       };
//     }
//     return depositsData;
//   };

//   const displayData = getDisplayData();
//   const summary = displayData?.summary || {};
//   const deposits = displayData?.deposits || [];

//   const rows = React.useMemo(() => {
//     return deposits.map((deposit, index) => ({
//       id: deposit.receiptNumber || deposit.checkoutRequestId || `deposit-${index}`,
//       ...deposit,
//       transactionDate: deposit.transactionDate ? new Date(deposit.transactionDate) : null,
//       createdAt: deposit.createdAt ? new Date(deposit.createdAt) : null,
//     }));
//   }, [deposits]);

//   const getColumns = React.useCallback(() => {
//     const baseColumns = [
//       { 
//         field: 'phone', 
//         headerName: 'Phone', 
//         width: 140,
//         minWidth: 120,
//         flex: 1,
//         renderCell: (params) => (
//           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//             <PhoneIcon sx={{ fontSize: 16, color: 'primary.main' }} />
//             <Typography 
//               variant="body2" 
//               sx={{ 
//                 fontFamily: 'monospace',
//                 fontSize: { xs: '0.75rem', md: '0.875rem' },
//                 fontWeight: searchMode ? 'bold' : 'normal',
//                 color: searchMode ? 'primary.main' : 'inherit'
//               }}
//             >
//               {params.value}
//             </Typography>
//           </Box>
//         )
//       },
//       { 
//         field: 'amount', 
//         headerName: 'Amount', 
//         width: isMobile ? 100 : 120, 
//         type: 'number',
//         minWidth: 90,
//         valueFormatter: (value) => `$${value?.toFixed(2) || '0.00'}`,
//         renderCell: (params) => (
//           <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//             <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
//             <Typography 
//               variant="body2" 
//               color="success.main"
//               sx={{ 
//                 fontWeight: 'medium',
//                 fontSize: { xs: '0.75rem', md: '0.875rem' }
//               }}
//             >
//               ${params.value?.toFixed(2) || '0.00'}
//             </Typography>
//           </Box>
//         )
//       },
//       { 
//         field: 'paymentSource', 
//         headerName: isMobile ? 'Source' : 'Payment Source', 
//         width: isMobile ? 100 : 140, 
//         minWidth: 90,
//         renderCell: (params) => (
//           <Chip 
//             label={params.value || 'N/A'}
//             color="primary"
//             size="small"
//             variant="outlined"
//             sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
//           />
//         )
//       },
//       { 
//         field: 'receiptNumber', 
//         headerName: isMobile ? 'Receipt' : 'Receipt Number', 
//         width: isMobile ? 120 : 160, 
//         minWidth: 100,
//         renderCell: (params) => (
//           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//             <ReceiptIcon sx={{ fontSize: 16, color: 'info.main' }} />
//             <Typography 
//               variant="body2" 
//               sx={{ 
//                 fontFamily: 'monospace',
//                 fontSize: { xs: '0.7rem', md: '0.8rem' },
//                 color: 'info.main'
//               }}
//             >
//               {params.value || 'N/A'}
//             </Typography>
//           </Box>
//         )
//       }
//     ];

//     if (!isMobile) {
//       baseColumns.push(
//         { 
//           field: 'transactionDate', 
//           headerName: 'Transaction Date', 
//           width: 140, 
//           type: 'dateTime',
//           valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : 'N/A'
//         }
//       );
//     }

//     if (!isTablet) {
//       baseColumns.push(
//         { 
//           field: 'userId', 
//           headerName: 'User ID', 
//           width: 120,
//           renderCell: (params) => (
//             <Typography 
//               variant="body2" 
//               sx={{ 
//                 fontFamily: 'monospace',
//                 fontSize: '0.8rem',
//                 color: 'text.secondary'
//               }}
//             >
//               {params.value || 'N/A'}
//             </Typography>
//           )
//         },
//         { 
//           field: 'createdAt', 
//           headerName: 'Created', 
//           width: 120, 
//           type: 'dateTime',
//           valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : 'N/A'
//         }
//       );
//     }

//     return baseColumns;
//   }, [isMobile, isTablet, searchMode]);

//   // Get unique payment sources for filter
//   const paymentSources = React.useMemo(() => {
//     const sources = new Set();
//     deposits.forEach(deposit => {
//       if (deposit.paymentSource) {
//         sources.add(deposit.paymentSource);
//       }
//     });
//     return Array.from(sources).sort();
//   }, [deposits]);

//   const summaryCards = [
//     { 
//       title: 'Total Records', 
//       value: summary.recordsReturned?.toLocaleString() || '0', 
//       color: 'primary.main', 
//       icon: <ReceiptIcon />, 
//       bgColor: theme.palette.primary.light 
//     },
//     { 
//       title: 'Total Amount', 
//       value: `$${summary.totalAmount?.toLocaleString() || '0.00'}`, 
//       color: 'success.main', 
//       icon: <AccountBalanceIcon />, 
//       bgColor: theme.palette.success.light 
//     },
//     { 
//       title: 'Average Deposit', 
//       value: `$${summary.recordsReturned > 0 ? (summary.totalAmount / summary.recordsReturned).toFixed(2) : '0.00'}`, 
//       color: 'info.main', 
//       icon: <TrendingUpIcon />, 
//       bgColor: theme.palette.info.light 
//     },
//     { 
//       title: 'Unique Users', 
//       value: new Set(deposits.map(d => d.userId)).size.toLocaleString() || '0', 
//       color: 'warning.main', 
//       icon: <PeopleIcon />, 
//       bgColor: theme.palette.warning.light 
//     }
//   ];

//   const LoadingSkeleton = () => (
//     <Box>
//       <Grid container spacing={3} sx={{ mb: 3 }}>
//         {[1, 2, 3, 4].map((index) => (
//           <Grid item xs={12} sm={6} md={3} key={index}>
//             <Card><CardContent><Skeleton variant="text" width="60%" height={32} /><Skeleton variant="text" width="40%" /></CardContent></Card>
//           </Grid>
//         ))}
//       </Grid>
//       <Paper sx={{ height: 400 }}><Skeleton variant="rectangular" height="100%" /></Paper>
//     </Box>
//   );

//   if (isLoading && !searchMode) {
//     return (
//       <Container maxWidth="xl">
//         <Box sx={{ py: 2 }}>
//           <Skeleton variant="text" width="200px" height={40} sx={{ mb: 1 }} />
//           <Skeleton variant="text" width="300px" height={24} sx={{ mb: 3 }} />
//           <LoadingSkeleton />
//         </Box>
//       </Container>
//     );
//   }

//   return (
//     <Container maxWidth="xl">
//       <Box sx={{ py: 2 }}>
//         {/* Header Section */}
//         <Box sx={{ 
//           display: 'flex', 
//           justifyContent: 'space-between', 
//           alignItems: { xs: 'flex-start', md: 'center' },
//           flexDirection: { xs: 'column', md: 'row' },
//           gap: 2,
//           mb: 3 
//         }}>
//           <Box>
//             <Typography 
//               variant={isMobile ? 'h5' : 'h4'}
//               component="h1" 
//               sx={{ fontWeight: 'bold', mb: 1 }}
//             >
//               Deposits {searchMode && <Chip label="Search Results" color="primary" size="small" sx={{ ml: 1 }} />}
//             </Typography>
//             <Typography variant="body1" color="text.secondary">
//               {searchMode ? `Search results for: ${searchQuery || 'filtered data'}` : 'All-time deposit transactions and payment analysis'}
//             </Typography>
//           </Box>
          
//           <Tooltip title="Refresh data">
//             <IconButton 
//               onClick={() => refetch()} 
//               disabled={isFetching || isSearching} 
//               color="primary" 
//               sx={{ 
//                 bgcolor: 'primary.light', 
//                 '&:hover': { bgcolor: 'primary.main', color: 'white' } 
//               }}
//             >
//               <RefreshIcon />
//             </IconButton>
//           </Tooltip>
//         </Box>

//         {/* Summary Cards */}
//         <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 3 }}>
//           {summaryCards.map((card, index) => (
//             <Grid item xs={6} sm={6} md={3} key={index}>
//               <Card sx={{ 
//                 height: '100%', 
//                 background: `linear-gradient(135deg, ${alpha(card.bgColor, 0.2)} 0%, ${alpha(card.bgColor, 0.1)} 100%)`, 
//                 border: `1px solid ${alpha(card.bgColor, 0.4)}`, 
//                 transition: 'transform 0.2s, box-shadow 0.2s', 
//                 '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[8] } 
//               }}>
//                 <CardContent sx={{ p: { xs: 2, md: 3 } }}>
//                   <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
//                     <Box sx={{ color: card.color, display: 'flex', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
//                       {card.icon}
//                     </Box>
//                     <Typography 
//                       component="div" 
//                       sx={{ 
//                         color: card.color, 
//                         fontWeight: 'bold', 
//                         fontSize: { xs: '1rem', md: '1.25rem' } 
//                       }}
//                     >
//                       {card.value}
//                     </Typography>
//                   </Stack>
//                   <Typography 
//                     variant="body2" 
//                     color="text.secondary" 
//                     sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
//                   >
//                     {card.title}
//                   </Typography>
//                 </CardContent>
//               </Card>
//             </Grid>
//           ))}
//         </Grid>

//         {/* Search and Filter Section */}
//         <Card sx={{ 
//           mb: 3, 
//           background: `linear-gradient(135deg, ${alpha(theme.palette.background.default, 0.6)} 0%, ${alpha(theme.palette.primary.light, 0.15)} 100%)`,
//           border: '1px solid',
//           borderColor: 'divider',
//           transition: 'transform 0.2s, box-shadow 0.2s', 
//           '&:hover': { transform: 'translateY(-1px)', boxShadow: theme.shadows[4] }
//         }}>
//           <CardContent sx={{ p: { xs: 2, md: 3 } }}>
//             <Stack spacing={2}>
//               {/* Search Bar */}
//               <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
//                 <TextField
//                   fullWidth
//                   placeholder="Search by phone number or receipt number..."
//                   value={searchInput}
//                   onChange={handleSearchInputChange}
//                   InputProps={{
//                     startAdornment: (
//                       <InputAdornment position="start">
//                         <SearchIcon color="action" />
//                       </InputAdornment>
//                     ),
//                     endAdornment: searchInput && (
//                       <InputAdornment position="end">
//                         <IconButton onClick={clearSearch} size="small">
//                           <ClearIcon />
//                         </IconButton>
//                       </InputAdornment>
//                     )
//                   }}
//                   sx={{
//                     '& .MuiOutlinedInput-root': {
//                       backgroundColor: theme.palette.background.paper,
//                     }
//                   }}
//                 />
                
//                 <Button
//                   variant="outlined"
//                   startIcon={<FilterListIcon />}
//                   onClick={() => setShowFilters(!showFilters)}
//                   sx={{ minWidth: 'fit-content' }}
//                 >
//                   Filters
//                 </Button>
                
//                 {isSearching && (
//                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 'fit-content' }}>
//                     <CircularProgress size={20} />
//                     <Typography variant="body2" color="text.secondary">Searching...</Typography>
//                   </Box>
//                 )}
//               </Stack>
              
//               {/* Filters */}
//               <Collapse in={showFilters}>
//                 <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ pt: 2 }}>
//                   <FormControl sx={{ minWidth: 200 }}>
//                     <InputLabel>Payment Source</InputLabel>
//                     <Select
//                       value={paymentSourceFilter}
//                       label="Payment Source"
//                       onChange={handlePaymentSourceChange}
//                     >
//                       <MenuItem value="">All Sources</MenuItem>
//                       {paymentSources.map((source) => (
//                         <MenuItem key={source} value={source}>
//                           {source}
//                         </MenuItem>
//                       ))}
//                     </Select>
//                   </FormControl>
//                 </Stack>
//               </Collapse>
//             </Stack>
//           </CardContent>
//         </Card>

//         {/* Search Results Alert */}
//         {searchMode && deposits.length === 0 && (
//           <Fade in>
//             <Alert severity="info" sx={{ mb: 3 }}>
//               <Typography variant="body2">
//                 No deposits found for the current search criteria.
//               </Typography>
//               <Button onClick={clearSearch} size="small" sx={{ mt: 1 }}>
//                 View All Deposits
//               </Button>
//             </Alert>
//           </Fade>
//         )}

//         {searchMode && deposits.length > 0 && (
//           <Fade in>
//             <Alert severity="success" sx={{ mb: 3 }}>
//               <Typography variant="body2">
//                 ✅ Found {deposits.length} deposit{deposits.length !== 1 ? 's' : ''}
//               </Typography>
//               <Button onClick={clearSearch} size="small" sx={{ mt: 1 }}>
//                 View All Deposits
//               </Button>
//             </Alert>
//           </Fade>
//         )}

//         {/* Error Handling */}
//         {(error || searchError) && (
//           <Alert severity="error" sx={{ mb: 3 }}>
//             Error loading deposit data: {error?.message || searchError?.message || 'Unknown error'}
//           </Alert>
//         )}

//         {/* Data Grid Section */}
//         <Paper sx={{ height: { xs: 500, md: 600 }, width: '100%', overflow: 'hidden' }}>
//           <DataGrid
//             rows={rows}
//             columns={getColumns()}
//             paginationModel={paginationModel}
//             onPaginationModelChange={setPaginationModel}
//             pageSizeOptions={isMobile ? [10, 25] : [25, 50, 100]}
//             rowCount={rows.length}
//             loading={isLoading || isFetching || isSearching}
//             disableRowSelectionOnClick
//             sx={{
//               '& .MuiDataGrid-root': { border: 'none' },
//               '& .MuiDataGrid-cell': { borderBottom: '1px solid', borderBottomColor: 'divider' },
//               '& .MuiDataGrid-columnHeaders': { 
//                 backgroundColor: alpha(theme.palette.primary.light, 0.1), 
//                 borderBottom: '2px solid', 
//                 borderBottomColor: 'divider' 
//               },
//               '& .MuiDataGrid-row:hover': { backgroundColor: 'action.hover' },
//               '& .MuiDataGrid-row:nth-of-type(even)': { 
//                 backgroundColor: alpha(theme.palette.action.hover, 0.5) 
//               },
//               ...(isMobile && {
//                 '& .MuiDataGrid-columnHeader': { fontSize: '0.75rem', fontWeight: 'bold' },
//                 '& .MuiDataGrid-cell': { fontSize: '0.75rem', py: 0.5 },
//               }),
//             }}
//             getRowId={(row) => row.id}
//             density={isMobile ? 'compact' : 'standard'}
//           />
//         </Paper>

//         {/* Data Info Footer */}
//         <Box sx={{ 
//           mt: 2, 
//           display: 'flex', 
//           justifyContent: 'space-between', 
//           alignItems: 'center', 
//           flexDirection: { xs: 'column', md: 'row' }, 
//           gap: 1 
//         }}>
//           <Typography variant="caption" color="text.secondary">
//             {searchMode && <Chip label="Filtered" size="small" sx={{ mr: 1 }} />}
//             Total Amount: ${summary.totalAmount?.toLocaleString() || '0.00'}
//           </Typography>
//           <Typography variant="caption" color="text.secondary">
//             Showing {rows.length} deposit{rows.length !== 1 ? 's' : ''} 
//             {searchMode ? ` (Search Results)` : ' (All-Time Data)'}
//           </Typography>
//         </Box>

//         {/* No deposits found */}
//         {!searchMode && !isLoading && deposits.length === 0 && (
//           <Alert severity="warning" sx={{ mt: 3 }}>
//             No deposit data available. This could be due to no transactions or database connectivity issues.
//           </Alert>
//         )}
//       </Box>
//     </Container>
//   );
// };

// export default Deposits;





// import React from 'react';
// import { Box, Typography, Paper } from '@mui/material';
// import { DataGrid } from '@mui/x-data-grid';

// const Deposits = () => {
//   const [loading, setLoading] = React.useState(false);
//   const [rows, setRows] = React.useState([]);
//   const [rowCount, setRowCount] = React.useState(0);
//   const [paginationModel, setPaginationModel] = React.useState({
//     page: 0,
//     pageSize: 25,
//   });

//   const columns = [
//     { field: 'id', headerName: 'ID', width: 90 },
//     { field: 'customerPhone', headerName: 'Customer', width: 150 },
//     { field: 'amount', headerName: 'Amount', width: 120, type: 'number' },
//     { field: 'method', headerName: 'Method', width: 150 },
//     { field: 'status', headerName: 'Status', width: 120 },
//     { field: 'createdAt', headerName: 'Date', width: 150, type: 'dateTime' },
//   ];

//   return (
//     <Box>
//       <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
//         Deposits
//       </Typography>
//       <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
//         Track customer deposits and transactions
//       </Typography>

//       <Paper sx={{ height: 600, width: '100%' }}>
//         <DataGrid
//           rows={rows}
//           columns={columns}
//           paginationModel={paginationModel}
//           onPaginationModelChange={setPaginationModel}
//           pageSizeOptions={[25, 50, 100]}
//           rowCount={rowCount}
//           loading={loading}
//           paginationMode="server"
//           sortingMode="server"
//           filterMode="server"
//           disableRowSelectionOnClick
//         />
//       </Paper>
//     </Box>
//   );
// };

// export default Deposits;